import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  User, 
  Bookmark,
  MousePointer,
  DollarSign,
  Crown,
  TrendingUp,
  Trash2
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [savedTrends, setSavedTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, savedRes] = await Promise.all([
        axios.get(`${API}/user/stats`, { headers }),
        axios.get(`${API}/user/saved-trends`, { headers })
      ]);

      setStats(statsRes.data);
      setSavedTrends(savedRes.data.saved_trends);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (trendId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/trends/${trendId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedTrends(savedTrends.filter(s => s.trend.trend_id !== trendId));
      toast.success("Trend removed from saved");
    } catch (error) {
      toast.error("Failed to remove trend");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200" />
          <div className="h-24 bg-gray-200" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Avatar */}
          <div className="relative">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-24 h-24 md:w-32 md:h-32 border-4 border-black object-cover"
              />
            ) : (
              <div className="w-24 h-24 md:w-32 md:h-32 bg-[#CCFF00] border-4 border-black flex items-center justify-center">
                <User className="w-12 h-12" />
              </div>
            )}
            {user.is_premium && (
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#FF0099] flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-extrabold tracking-tighter">
                {user.name}
              </h1>
              {user.is_premium && (
                <span className="bg-[#FF0099] text-white text-xs px-2 py-1 font-bold uppercase">
                  Premium
                </span>
              )}
            </div>
            <p className="text-gray-500 font-mono text-sm mb-4">{user.email}</p>
            <div className="flex gap-2">
              <Link to="/explore">
                <Button
                  className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold"
                  data-testid="explore-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Explore Trends
                </Button>
              </Link>
              {!user.is_premium && (
                <Link to="/payment">
                  <Button
                    variant="outline"
                    className="border-2 border-black font-bold hover:bg-[#CCFF00]"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Pro
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8"
      >
        <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
          YOUR STATS
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="neo-card p-6 text-center">
            <Bookmark className="w-6 h-6 mx-auto mb-2 text-[#7C3AED]" />
            <div className="text-3xl font-bold">{stats?.saved_trends || 0}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Saved Trends</div>
          </div>
          <div className="neo-card p-6 text-center">
            <MousePointer className="w-6 h-6 mx-auto mb-2 text-[#FF0099]" />
            <div className="text-3xl font-bold">{stats?.total_clicks || 0}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Affiliate Clicks</div>
          </div>
          <div className="neo-card p-6 text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-[#CCFF00]" />
            <div className="text-3xl font-bold">${stats?.total_earnings?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Earnings</div>
          </div>
          <div className="neo-card p-6 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-black" />
            <div className="text-3xl font-bold">{stats?.affiliate_links || 0}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Affiliate Links</div>
          </div>
        </div>
      </motion.div>

      {/* Saved Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500">
            SAVED TRENDS ({savedTrends.length})
          </h2>
        </div>

        {savedTrends.length > 0 ? (
          <div className="space-y-3">
            {savedTrends.map((saved) => (
              <div
                key={saved.save_id}
                className="neo-card p-4 flex items-center justify-between"
              >
                <Link 
                  to={`/trend/${saved.trend.trend_id}`}
                  className="flex-1 hover:text-[#FF0099] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-12 ${
                      saved.trend.velocity === "rising" ? "bg-green-500" :
                      saved.trend.velocity === "declining" ? "bg-red-500" : "bg-gray-400"
                    }`} />
                    <div>
                      <h3 className="font-bold">{saved.trend.keyword}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{saved.trend.category}</span>
                        <span className={
                          saved.trend.velocity === "rising" ? "text-green-600" :
                          saved.trend.velocity === "declining" ? "text-red-600" : "text-gray-600"
                        }>
                          {saved.trend.growth_rate > 0 ? "+" : ""}{saved.trend.growth_rate?.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-mono">
                    Saved {formatDate(saved.saved_at)}
                  </span>
                  <button
                    onClick={() => handleUnsave(saved.trend.trend_id)}
                    className="p-2 hover:bg-red-50 text-red-500 transition-colors"
                    data-testid={`unsave-${saved.trend.trend_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 neo-card">
            <Bookmark className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-bold mb-2">No saved trends yet</h3>
            <p className="text-gray-500 mb-6">
              Start saving trends to track them and get notified of changes!
            </p>
            <Link to="/explore">
              <Button className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold">
                <TrendingUp className="w-4 h-4 mr-2" />
                Explore Trends
              </Button>
            </Link>
          </div>
        )}
      </motion.div>

      {/* Affiliate CTA for non-premium */}
      {!user.is_premium && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 neo-card p-6 bg-[#FF0099]/10 border-[#FF0099]"
        >
          <div className="flex items-center gap-4">
            <DollarSign className="w-10 h-10 text-[#FF0099]" />
            <div className="flex-1">
              <h3 className="font-bold">Unlock Affiliate Program</h3>
              <p className="text-sm text-gray-600">
                Upgrade to Premium and earn 10% commission on sales from your shared affiliate links.
              </p>
            </div>
            <Link to="/payment">
              <Button className="bg-[#FF0099] text-white border-2 border-black font-bold">
                Upgrade Now
              </Button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ProfilePage;
